'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { FileUploadWidget } from './FileUploadWidget';

interface LoadExcelConfigProps {
  nodeId: string;
  config: { fileName?: string; rowCount?: number; table?: string; headerRow?: number; tableFormat?: string };
  /** Persisted DAG operator type (e.g. load_table when embedded in Load Table node) */
  persistType?: string;
  /** Merged into every params save (e.g. { tableFormat: 'excel' }) */
  paramDefaults?: Record<string, unknown>;
}

export function LoadExcelConfig({ nodeId, config, persistType = 'load_excel', paramDefaults }: LoadExcelConfigProps) {
  const { t } = useTranslation();
  const { updateNodeConfig } = useWorkflowStore();
  const [table, setTable] = useState(config.table || '');
  const headerRow = config.headerRow ?? 1;

  // 当 config 变化时（如 AI 生成工作流后），同步更新内部状态
  useEffect(() => {
    if (config.table) setTable(config.table);
  }, [config.table]);

  const mergeParams = (p: Record<string, unknown>) => ({
    ...(paramDefaults ?? {}),
    ...p,
  });

  const saveTable = (val: string) => {
    updateNodeConfig(nodeId, {
      type: persistType,
      params: mergeParams({ fileName: config.fileName, rowCount: config.rowCount, table: val, headerRow }),
    });
  };

  const saveHeaderRow = (val: number) => {
    const n = Math.max(1, Math.min(999, val));
    updateNodeConfig(nodeId, {
      type: persistType,
      params: mergeParams({ fileName: config.fileName, rowCount: config.rowCount, table: config.table, headerRow: n }),
    });
  };

  return (
    <div className="space-y-5">
      {/* File upload area */}
      <FileUploadWidget 
        nodeId={nodeId} 
        accept="excel" 
        persistType={persistType}
        paramDefaults={paramDefaults}
        headerRow={headerRow} 
        onHeaderRowDetected={(detectedRow) => {
          // 自动更新表头行设置
          if (detectedRow !== headerRow) {
            saveHeaderRow(detectedRow);
          }
        }}
      />

      {/* Header row (1-based) */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-500">
          {t('nodes.config.load.headerRow')}
        </label>
        <input
          type="number"
          min={1}
          max={999}
          value={headerRow}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            if (!Number.isNaN(v)) saveHeaderRow(v);
          }}
          className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
        />
        <p className="text-[10px] text-zinc-400">
          {t('nodes.config.load.headerHint')}
        </p>
      </div>

      {/* Table alias */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-500">
          {t('nodes.config.load.tableAlias')}
        </label>
        <input
          type="text"
          placeholder={t('nodes.config.load.tableAliasPlaceholder')}
          value={table}
          onChange={(e) => { setTable(e.target.value); saveTable(e.target.value); }}
          className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 placeholder:text-zinc-400"
        />
        <p className="text-[10px] text-zinc-400">
          {t('nodes.config.load.tableAliasHelp')}
        </p>
      </div>
    </div>
  );
}
